import clsx from "clsx";
import { IconChevronDown } from "@tabler/icons-react";
import { useRouter } from "next/router";
import { useMemo } from "react";

import {
  BRAND_LOGGED_IN_START_PAGE,
  BRAND_PATHS,
  language,
  ORDER_STATUS_IDS,
  ORDER_TYPE_IDS,
} from "@chef/constants";
import {
  useAppDispatch,
  useAppSelector,
  useCalendarData,
} from "@chef/state-management/hooks";
import {
  formatDate,
  getNextCutoff,
  getWeekYearCutoffByDate,
  isMealboxBasketType,
  orderHasBeenDelivered,
} from "@chef/helpers";
import { Skeleton } from "@chef/components";
import {
  showMyMenuSelectWeekModal,
  useOrdersQuery,
} from "@chef/state-management";
import { shouldShowDelivery } from "@chef/helpers";
import { isEqualStrings } from "@chef/utils/equal";

import { Logo } from "../Logo";
import { Drawer } from "./Drawer";

const intl = (
  {
    no: {
      WEEK: "Uke",
      SHORT_DATE_FORMAT: "EEE d. MMM",
      CANCELLED: "Avbestilt",
      CLOSED: "Stengt",
      DELIVERED: "Levert",
      IN_TRANSIT: "Underveis",
      LONG_DATE_FORMAT: "d. MMM HH:mm",
      MAKE_CHANGES_BY: "Endre innen",
    },
    se: {
      WEEK: "Vecka",
      SHORT_DATE_FORMAT: "EEE d MMM",
      CANCELLED: "Avbokad",
      CLOSED: "Stängt",
      DELIVERED: "Levererad",
      IN_TRANSIT: "På väg",
      LONG_DATE_FORMAT: "d MMM HH:mm",
      MAKE_CHANGES_BY: "Ändra senast",
    },
    dk: {
      WEEK: "Uge",
      SHORT_DATE_FORMAT: "EEE d. MMM",
      CANCELLED: "Afbestilt",
      CLOSED: "Lukket",
      DELIVERED: "Leveret",
      IN_TRANSIT: "Undervejs",
      LONG_DATE_FORMAT: "d. MMM HH:mm",
      MAKE_CHANGES_BY: "Foretag ændringer senest",
    },
  } as const
)[language];

export const MyMenuVariant = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { week: nextCutoffWeek, year: nextCutoffYear } = getNextCutoff();
  const { week: queryWeek, year: queryYear } = router.query;

  const week = queryWeek ? Number(queryWeek) : nextCutoffWeek;
  const year = queryYear ? Number(queryYear) : nextCutoffYear;

  const handleOpenDialog = () => {
    dispatch(showMyMenuSelectWeekModal());
  };

  const isMyMenuSelectWeekModalOpen = useAppSelector(
    (state) => state.modal.myMenuSelectWeek.open,
  );

  const { calendar, isLoading: isLoadingCalendar } = useCalendarData({
    week: nextCutoffWeek,
    year: nextCutoffYear,
  });

  const { order, isLoading: isLoadingOrder } = useOrdersQuery(
    {
      count: 1,
      orderTypes: [ORDER_TYPE_IDS.RECURRING],
      orderStatuses: [ORDER_STATUS_IDS.PROCESSING, ORDER_STATUS_IDS.FINISHED],
    },
    {
      selectFromResult: ({ data, isLoading }) => ({
        order:
          data?.orders[0] && shouldShowDelivery(data.orders[0].deliveryDate)
            ? {
                deliveryDate: data.orders[0].deliveryDate,
                hasBeenDelivered: orderHasBeenDelivered(data.orders[0]),
              }
            : undefined,
        isLoading,
      }),
    },
  );

  const deliveryDateMessage = useMemo(() => {
    const isOnDeliveryPage = isEqualStrings(
      router.pathname,
      BRAND_PATHS.MY_DELIVERY,
    );

    if (isOnDeliveryPage) {
      return formatDate(
        order?.deliveryDate ?? new Date().getTime(),
        intl.SHORT_DATE_FORMAT,
      );
    }

    const deliveryDate = calendar
      ?.find((cw) => cw.week === week && cw.year === year)
      ?.baskets?.find((basket) => isMealboxBasketType(basket))?.shippingDetails
      .plannedDeliveryInformation.deliveryDate;

    return formatDate(
      new Date(deliveryDate ?? new Date().getTime()),
      intl.SHORT_DATE_FORMAT,
    );

    return;
  }, [order, router, calendar, week, year]);

  const deliveryStatusMessage = useMemo(() => {
    const isOnDeliveryPage = isEqualStrings(
      router.pathname,
      BRAND_PATHS.MY_DELIVERY,
    );

    if (isOnDeliveryPage) {
      return order?.hasBeenDelivered ? intl.DELIVERED : intl.IN_TRANSIT;
    }

    const deliveryDate = calendar
      ?.find((cw) => cw.week === week && cw.year === year)
      ?.baskets?.find((basket) => isMealboxBasketType(basket))?.shippingDetails
      .plannedDeliveryInformation.deliveryDate;

    const date = getWeekYearCutoffByDate(
      new Date(deliveryDate ?? new Date().getTime()),
    );

    return `${intl.MAKE_CHANGES_BY} ${formatDate(date, intl.LONG_DATE_FORMAT)}`;
  }, [order, router, calendar, week, year]);

  return (
    <div
      id="my-menu-header"
      className="relative flex items-center justify-between w-full h-15"
    >
      <div className="flex shrink">
        <Logo responsive href={BRAND_LOGGED_IN_START_PAGE} />
      </div>

      <div className="absolute flex items-center justify-center gap-8 transform -translate-x-1/2 left-1/2">
        {isLoadingCalendar || isLoadingOrder ? (
          <Skeleton
            animation="pulse"
            height={10}
            width={50}
            variant="rounded"
          />
        ) : (
          <button
            type="button"
            onClick={handleOpenDialog}
            className="flex flex-col items-center ml-2"
          >
            <div className="flex items-center justify-center gap-1">
              <strong className="text-sm">{deliveryDateMessage}</strong>
              <IconChevronDown
                size={16}
                className={clsx(
                  "transition-transform duration-350",
                  isMyMenuSelectWeekModalOpen && "rotate-180",
                )}
              />
            </div>
            <p className="text-sm text-grey-1">{deliveryStatusMessage}</p>
          </button>
        )}
      </div>

      <div className="flex items-center justify-end shrink">
        <Drawer />
      </div>
    </div>
  );
};
